import { z } from 'zod';
import { REMINDER_COLLAGE_MAX_ITEMS, REMINDER_MEDIA_CAPTION_MAX, TELEGRAM_RICH_MEDIA_MAX, } from '@irl/types';
import { objectIdSchema } from './common.js';
const reminderRichContentSchema = z.lazy(() => z.discriminatedUnion('type', [
    z.object({ type: z.literal('plain'), text: z.string() }),
    z.object({ type: z.literal('bold'), text: z.string() }),
    z.object({ type: z.literal('italic'), text: z.string() }),
    z.object({ type: z.literal('underline'), text: z.string() }),
    z.object({ type: z.literal('strikethrough'), text: z.string() }),
    z.object({ type: z.literal('spoiler'), text: z.string() }),
    z.object({ type: z.literal('code'), text: z.string() }),
    z.object({
        type: z.literal('dateTime'),
        timestamp: z.number(),
        text: z.string().optional(),
    }),
    z.object({
        type: z.literal('composite'),
        parts: z.array(reminderRichContentSchema),
    }),
]));
const reminderRichMediaRefSchema = z.object({
    mediaId: objectIdSchema,
    telegramFileId: z.string().min(1).max(256),
});
const reminderRichBlockSchema = z
    .object({
    type: z.enum([
        'paragraph',
        'sectionHeading',
        'preformatted',
        'list',
        'blockQuotation',
        'pullQuotation',
        'table',
        'details',
        'divider',
        'footer',
        'dateTime',
        'photo',
        'collage',
        'slideshow',
    ]),
    content: reminderRichContentSchema,
    items: z.array(reminderRichContentSchema).optional(),
    rows: z
        .array(z.object({
        cells: z.array(reminderRichContentSchema),
        isHeader: z.boolean().optional(),
    }))
        .optional(),
    summary: reminderRichContentSchema.optional(),
    caption: z.string().max(REMINDER_MEDIA_CAPTION_MAX).optional(),
    isOrdered: z.boolean().optional(),
    headingSize: z
        .union([z.literal(1), z.literal(2), z.literal(3), z.literal(4), z.literal(5), z.literal(6)])
        .optional(),
    mediaId: objectIdSchema.optional(),
    telegramFileId: z.string().min(1).max(256).optional(),
    mediaItems: z.array(reminderRichMediaRefSchema).max(REMINDER_COLLAGE_MAX_ITEMS).optional(),
})
    .superRefine((block, ctx) => {
    if (block.type === 'photo') {
        if (!block.mediaId || !block.telegramFileId) {
            ctx.addIssue({
                code: 'custom',
                message: 'Photo block requires uploaded media',
                path: ['mediaId'],
            });
        }
        return;
    }
    if (block.type === 'collage' || block.type === 'slideshow') {
        if (!block.mediaItems || block.mediaItems.length === 0) {
            ctx.addIssue({
                code: 'custom',
                message: 'Collage and slideshow require at least one photo',
                path: ['mediaItems'],
            });
        }
        if (block.mediaItems && block.mediaItems.length > REMINDER_COLLAGE_MAX_ITEMS) {
            ctx.addIssue({
                code: 'custom',
                message: `Collage and slideshow can contain at most ${String(REMINDER_COLLAGE_MAX_ITEMS)} photos`,
                path: ['mediaItems'],
            });
        }
    }
});
export const reminderRichMessageSchema = z
    .object({
    blocks: z.array(reminderRichBlockSchema).max(500),
})
    .superRefine((message, ctx) => {
    const mediaCount = countRichMessageMedia(message);
    if (mediaCount > TELEGRAM_RICH_MEDIA_MAX) {
        ctx.addIssue({
            code: 'custom',
            message: `A rich message can contain at most ${String(TELEGRAM_RICH_MEDIA_MAX)} media files`,
            path: ['blocks'],
        });
    }
});
export function collectRichMediaRefs(message) {
    const refs = [];
    for (const block of message.blocks) {
        if (block.type === 'photo' && block.mediaId && block.telegramFileId) {
            refs.push({ mediaId: block.mediaId, telegramFileId: block.telegramFileId });
        }
        if ((block.type === 'collage' || block.type === 'slideshow') &&
            Array.isArray(block.mediaItems)) {
            refs.push(...block.mediaItems);
        }
    }
    return refs;
}
function countRichMessageMedia(message) {
    return collectRichMediaRefs(message).length;
}
function isCompleteMediaBlock(block) {
    if (block.type === 'photo') {
        return Boolean(block.mediaId && block.telegramFileId);
    }
    if (block.type === 'collage' || block.type === 'slideshow') {
        return (block.mediaItems?.length ?? 0) > 0;
    }
    return true;
}
export function stripIncompleteMediaBlocks(message) {
    return {
        blocks: message.blocks.filter((block) => isCompleteMediaBlock(block)),
    };
}
export const reminderMediaIdParamSchema = z.object({
    id: objectIdSchema,
});
//# sourceMappingURL=rich-message.js.map